/**
 * PATH: packages/bills/src/components/BillFieldPrimitives.tsx
 * FILE_ID: BILLS-PKG-CMP-FIELD-007
 * PURPOSE: Presentational form primitives for the bill data panel — the
 *          labelled <Field/>, the <Section/> grouping, and the <Empty/>
 *          filler.
 *
 * WHY: Extracted from BillDataPanel.tsx (P4-1 split). The per-field
 *      "pick from PDF" crosshair button that used to live here was removed
 *      (operator: too much visual noise) — arming a field for document
 *      capture now happens by clicking/focusing the input itself, which
 *      BillDataPanel wires via its data-bill-field click/focus delegation.
 */

import { FileText } from 'lucide-react';

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-2.5 rounded-lg border border-border bg-card p-3 shadow-sm">
      <h3 className="mb-2 text-xs font-semibold tracking-tight text-foreground">{title}</h3>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted/30 p-6 text-center">
      <div className="flex max-w-[17rem] flex-col items-center gap-3.5 rounded-2xl border border-border bg-card px-6 py-8 shadow-sm">
        <span
          aria-hidden
          className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"
        >
          <FileText className="h-5 w-5" />
        </span>
        <p className="text-sm text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}
